/**
 * Resending an add-a-school approval request — the ONE model behind the button.
 *
 * `POST /companies/:id/approval-request` is mounted on two surfaces (the account
 * detail page and a row action on `/companies`), so everything that decides what
 * the reader is told lives here: which companies are resendable, how a `201` is
 * worded, and how the three refusals are routed. Two copies of this would agree
 * the day they were written and drift on the first correction — and the thing
 * that would drift is a sentence about whether a customer-facing email went out.
 *
 * ## ⚠️ The `201` is not a receipt, and that is the whole reason this file exists
 *
 * The endpoint does a database write and then a notify, and **the notify never
 * throws for an organisational reason**. Nobody in the approvers group, a mailer
 * that queued nothing, an enqueue that could not write its row — every one
 * answers `201`, with a body that says so in numbers. A `201` means a round was
 * OPENED; only `recipientsQueued` says whether anybody was told. So the outcome
 * is worded from `approversFound` / `recipientsQueued` / `supersededLinks` and
 * **never from the status code**.
 *
 * ⚠️ **`emailsSent` IS GONE, AND THIS FILE READ IT UNTIL 2026-08-30.** It counted
 * messages the relay accepted, one per approver; the owner's one-shared-email
 * ruling makes that `0` or `1` for any size of group, so the backend DELETED the
 * key rather than renaming it — a client still computing `emailsSent ===
 * approversFound` would have rendered *"reached 1 of 2 approvers"* on a
 * completely successful send, and reading a deleted key yields `undefined`, which
 * shows as a gap rather than as a plausible lie.
 *
 * This is the proxy-field rule in `CLAUDE.md` again: the status answers *"did the
 * request succeed"*, which is not the question the person who pressed the button
 * is asking. Reporting the verb pressed rather than the outcome recorded is how
 * somebody comes away believing an approver was emailed when none exists.
 */

import { ApiError } from '@/lib/api';
import { formatDateTime, pluralise } from '@/lib/format';
import { denialReason, type Ability } from '@/lib/permissions';
import type {
  CompanyApprovalResendResult,
  CompanyApprovalStatus,
  CompanyCreateApprovalRequest,
} from '@/lib/types';

/* --- who may press it ---------------------------------------------------- */

/**
 * `POST /companies/:id/approval-request` is `ADMIN_ROLES`, so the gate is
 * `ability.isAdmin` — **the ability object, never `user.role`, and never
 * `/permissions/me`**, which is advisory and wrong in both directions.
 *
 * ⚠️ **No `ModuleSlug` is invented for this and no neighbouring one is
 * borrowed.** `companies.update` is held by `basic_user`, so gating on it would
 * enable the control for people the API answers `403` — the over-reporting
 * direction `@/lib/permissions` exists to prevent. `denialReason()` cannot be
 * called without a slug, so the sentences are written out here; they match that
 * helper's own admin and read-only wording deliberately, so the app says one
 * thing about one situation.
 */

export function resendDenialFor(ability: Ability): string | null {
  /*
   * 🔴 2026-10-01: the backend gates this route on `companies.update` (measured
   * in `companies.routes.ts`), and the set decides for every role. *SUPERSEDED,
   * the doc above: `ADMIN_ROLES`, and "gating on `companies.update` would
   * over-report".*
   */
  return denialReason(ability, 'update', 'companies');
}

/* --- which companies are resendable -------------------------------------- */

/**
 * A resend applies to a company still waiting on a decision, and nothing else.
 *
 * Safe to match exactly because {@link CompanyApprovalStatus} is a literal union
 * — the flow itself, not admin-editable reference data. This is the one place in
 * the app where matching on a status VALUE is right; a stage or an invoice status
 * would be the free-text trap `CLAUDE.md` records three times.
 *
 * ⚠️ **`approvalStatus` is already on every `GET /companies` row and on
 * `GET /companies/{id}`** — no extra request and no new field. Do not fetch the
 * approval to answer this.
 */
export function isResendable(company: { approvalStatus: CompanyApprovalStatus }): boolean {
  return company.approvalStatus === 'pending';
}

/**
 * Why the button is inert on a company that is not pending.
 *
 * ⚠️ **Returned rather than the control being hidden, on the detail page at
 * least.** An absent affordance is indistinguishable from a removed feature, and
 * this codebase has had exactly that reported as a bug — the companies "+" menu,
 * which had only become unreachable. A disabled control with a sentence says
 * which of the two it is.
 *
 * Routes through {@link isResendable} rather than repeating the comparison, so
 * "which companies may be resent" has one answer and the button cannot come to
 * disagree with the sentence explaining it.
 */
export function notPendingReason(status: CompanyApprovalStatus): string | null {
  if (isResendable({ approvalStatus: status })) return null;
  return status === 'approved'
    ? 'This school has already been approved, so there is nothing awaiting a decision.'
    : 'This school was rejected, so there is nothing awaiting a decision.';
}

/* --- the 201, worded from the counts ------------------------------------- */

/**
 * What to tell the person who pressed the button.
 *
 * `tone` drives colour only, and never carries meaning alone — every branch says
 * what happened in words, because colour as the only signal is a house rule.
 */
export interface ResendOutcome {
  tone: 'success' | 'warning' | 'danger';
  /** The headline: what happened, in one sentence. */
  headline: string;
  /**
   * Consequences and next steps, each its own sentence.
   *
   * Empty on the ordinary success — there is nothing to add to "it went out".
   */
  notes: string[];
}

/**
 * Turn a `201` into a sentence, from the counts alone.
 *
 * The four cases, in the order they are tested. Each failing one is tested
 * BEFORE the happy path so that no reading of this can report success by falling
 * through:
 *
 *  1. **`approversFound === 0` — the important one, and it must not read as
 *     success.** Nothing was emailed, and nothing can ever be approved, because
 *     there is nobody to approve it. A "we could not send it" sentence would miss
 *     the second half and send an administrator retrying a button that will keep
 *     answering `201`. The fix is a person being put in the group.
 *  2. **`recipientsQueued === 0` with approvers present** — the group is fine and
 *     nothing was queued. Different problem, different destination: Settings,
 *     which is where mail health is visible.
 *  3. **`recipientsQueued < approversFound`** — see the warning below; the
 *     destination is **Users**, not the mailer.
 *  4. **`supersededLinks > 0`** — reported on TOP of whatever else happened,
 *     never instead of it, because both are true at once. Anyone still holding an
 *     older link now gets "this link is no longer valid" with nothing explaining
 *     why, so the sender is the only person who can warn them.
 *
 * ⚠️ **CASE 3 CHANGED ITS MEANING ON 2026-08-30 WITHOUT CHANGING ITS SHAPE, AND
 * THE OLD COPY POINTED AT THE WRONG SCREEN.** `emailsSent < approversFound` meant
 * *some sends failed* — a mailer problem. `recipientsQueued < approversFound`
 * means **some approvers have no email address on their account**: there is one
 * message, so everybody with an address is on it or nobody is, and the shortfall
 * can only be missing addresses. That is fixed under **Users**. Sending somebody
 * to check a mail server that is working is the reassuring-direction failure this
 * codebase keeps writing up, one screen further along.
 *
 * ⚠️ **"Queued", never "sent" or "delivered", on every branch that reports a
 * positive count.** The message is written to an outbound table and a worker
 * sends it, so this reports what was accepted for sending and nothing about what
 * landed. The zero cases may say nothing was sent, because that understates
 * rather than overstates.
 */
export function describeResendOutcome(result: CompanyApprovalResendResult): ResendOutcome {
  const { approversFound, recipientsQueued, supersededLinks } = result;

  /*
   * Built first and appended to every branch. `supersededLinks` is orthogonal to
   * whether anything was queued — a `??` or an `else` between the two would drop
   * one of two facts that are both true, which is the silent-drop shape
   * `CLAUDE.md` lists four instances of.
   *
   * ⚠️ The second sentence FORKS on whether anything was queued, and it must.
   * "A new one is on its way" is a promise, and on the two zero branches below it
   * is false — the old links are dead and no replacement went anywhere, which
   * leaves every approver holding nothing. Reassuring somebody that a link is
   * coming when none is, is worse than the superseding it was reporting.
   *
   * ⚠️ It counts LINKS, not approvers, and a round now mints ONE link for the
   * whole group — so expect `1` where this used to read `2`. `pluralise` from the
   * number; never read it as a headcount.
   */
  const superseded =
    supersededLinks > 0
      ? [
          `${pluralise(supersededLinks, 'earlier approval link')} stopped working just now. ` +
            (recipientsQueued > 0
              ? 'Anyone holding one will be told it is invalid; a new one is on its way.'
              : 'Anyone holding one will be told it is invalid, and no replacement has gone out.'),
        ]
      : [];

  if (approversFound === 0) {
    return {
      tone: 'danger',
      headline: 'Nobody is in the approvers group, so nothing was sent.',
      notes: [
        // The half a "sending failed" sentence would miss, and the reason this
        // is `danger` rather than `warning`: retrying cannot fix it.
        'Resending will not help until somebody is in that group.',
        'Add an approver under Users, then send this again.',
        ...superseded,
      ],
    };
  }

  if (recipientsQueued === 0) {
    return {
      tone: 'danger',
      /*
       * SINGULAR whatever the group size, because there is one message. The
       * previous version branched on `approversFound === 1` to avoid "none of the
       * 1 approval emails"; one shared email removes the plural case entirely
       * rather than making it rarer.
       */
      headline: 'The request was recorded, but no approval email was queued.',
      notes: [
        'The group is fine, so this is the mail server. Check it on Settings.',
        ...superseded,
      ],
    };
  }

  if (recipientsQueued < approversFound) {
    const missing = approversFound - recipientsQueued;
    return {
      tone: 'warning',
      headline: `The approval email is queued to ${recipientsQueued} of ${approversFound} approvers.`,
      notes: [
        /*
         * ⚠️ USERS, NOT THE MAIL SERVER. There is one message: everybody who has
         * an address is on it, so a shortfall is missing addresses and nothing
         * else. The shortfall is named as a count because it is the only part the
         * reader can act on — except at one, where `pluralise` would give "The
         * other 1 approver" and the bare noun reads as English.
         */
        (missing === 1 ? 'The other approver has' : `The other ${missing} approvers have`) +
          ' no email address recorded. Add them under Users.',
        ...superseded,
      ],
    };
  }

  return {
    tone: supersededLinks > 0 ? 'warning' : 'success',
    headline: `The approval email is queued to ${pluralise(recipientsQueued, 'approver')}.`,
    notes: superseded,
  };
}

/* --- the 201 of POST /companies, worded ---------------------------------- */

/**
 * What the New company confirmation says about the approval round the create
 * opened (owner, 2026-10-16: a headline and at most one short fact line, no
 * explanations or next-step advice).
 *
 * `line: null` means say nothing beyond the headline. `offerResend` is true only
 * on the failure branches. Failures are tested before the queued branch so no
 * reading of this falls through to a success.
 *
 * ⚠️ "Sent" is the owner's chosen word (2026-10-16) for `recipientsQueued > 0`;
 * it is never "delivered".
 */
export interface NewCompanyApprovalOutcome {
  tone: 'success' | 'warning';
  line: string | null;
  offerResend: boolean;
}

export function describeNewCompanyApproval(
  request: CompanyCreateApprovalRequest | null | undefined,
): NewCompanyApprovalOutcome {
  // Older API (key absent) or no round reported: no fact to state.
  if (request === undefined || request === null) {
    return { tone: 'success', line: null, offerResend: false };
  }
  if (!request.raised) {
    return { tone: 'warning', line: 'Approval request not recorded.', offerResend: true };
  }
  if (request.approversFound === 0) {
    return { tone: 'warning', line: 'No approvers set up — nobody was asked.', offerResend: true };
  }
  if (request.recipientsQueued > 0) {
    return {
      tone: 'success',
      line: `Sent to ${pluralise(request.recipientsQueued, 'approver')}.`,
      offerResend: false,
    };
  }
  if (!request.mailConfigured) {
    return {
      tone: 'warning',
      line: 'Approval email not sent — mail server not set up.',
      offerResend: true,
    };
  }
  return { tone: 'warning', line: 'Approval email not sent.', offerResend: true };
}

/**
 * The confirmation headline. Only a `pending` company was submitted for approval.
 * Owner 2026-10-27: when the Billing name (the Xero Contact Name) differs from
 * the Company name it rides in the same sentence, "X (billed as Y) …". Absent,
 * blank or equal (trimmed): the Company name alone.
 */
export function newCompanyHeadline(company: {
  name: string;
  billingName?: string | null;
  approvalStatus: CompanyApprovalStatus;
}): string {
  const billing = company.billingName?.trim() ?? '';
  const subject =
    billing === '' || billing === company.name.trim()
      ? company.name
      : `${company.name} (billed as ${billing})`;
  return isResendable(company) ? `${subject} submitted for approval` : `${subject} created`;
}

/* --- the refusals -------------------------------------------------------- */

/**
 * Already approved or rejected. **Terminal** — there is no retry that helps, and
 * the record's own `approvalStatus` is the thing that changed underneath.
 */
export const APPROVAL_NOT_PENDING_CODE = 'COMPANY_APPROVAL_NOT_PENDING';

/**
 * Sent within the last five minutes.
 *
 * ⚠️ **NOT terminal, and that is the whole reason this is matched separately
 * from its 409 neighbours** — it clears on its own, so the control comes back.
 * Rendering it like the other two tells somebody a resend is impossible when it
 * is merely early.
 */
export const APPROVAL_RESENT_TOO_RECENTLY_CODE = 'COMPANY_APPROVAL_RESENT_TOO_RECENTLY';

/** The status column holds something this flow cannot read. Stop; do not retry. */
export const APPROVAL_STATUS_UNRECOGNISED_CODE = 'COMPANY_APPROVAL_STATUS_UNRECOGNISED';

/**
 * The cooldown facts the 429 carries, once every field has been proven.
 *
 * Every member is independently optional because each is guarded independently —
 * a server that stops sending one must not take the other two with it.
 */
export interface ResendCooldown {
  /** Whole seconds until the next attempt is allowed. Non-negative. */
  retryAfterSeconds: number | null;
  /** An ISO timestamp, already proven to be a parseable date. */
  nextAllowedAt: string | null;
  /** An ISO timestamp, already proven to be a parseable date. */
  lastRequestedAt: string | null;
}

/** A timestamp is only useful once it is known to render — an unparseable string
 *  formats as `Invalid Date`, which reads as a bug in the app rather than as a
 *  server that sent something odd. */
function isoOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return Number.isNaN(Date.parse(trimmed)) ? null : trimmed;
}

/**
 * Read the 429's cooldown facts.
 *
 * ⚠️ **`details` is an OBJECT here, not the per-field array**, so
 * `ApiError.details` — narrowed with `Array.isArray` — is `undefined` and the
 * payload sits on `detailPayload` as `unknown`. Guarded field by field, **never
 * cast**, exactly as `xeroMessages` and `detailReasons` are: a cast would let a
 * missing key reach a renderer as `undefined` and print as nothing, or reach
 * arithmetic as `NaN` and print a countdown of `NaN seconds`.
 *
 * ⚠️ **And never parse the sentence.** The retry time appears in the server's
 * prose too, and lifting it out of there is matching on wording by another name —
 * the message is free to be reworded, the fields are the contract.
 *
 * Returns `null` when nothing usable was carried, which is an ordinary answer: a
 * caller then shows the server's own sentence with no countdown rather than
 * inventing one.
 */
export function resendCooldownOf(error: ApiError): ResendCooldown | null {
  const payload = error.detailPayload;
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  const record = payload as Record<string, unknown>;

  const rawSeconds = record.retryAfterSeconds;
  const retryAfterSeconds =
    typeof rawSeconds === 'number' && Number.isFinite(rawSeconds) && rawSeconds >= 0
      ? Math.ceil(rawSeconds)
      : null;

  const cooldown: ResendCooldown = {
    retryAfterSeconds,
    nextAllowedAt: isoOrNull(record.nextAllowedAt),
    lastRequestedAt: isoOrNull(record.lastRequestedAt),
  };

  return cooldown.retryAfterSeconds === null &&
    cooldown.nextAllowedAt === null &&
    cooldown.lastRequestedAt === null
    ? null
    : cooldown;
}

/**
 * A refusal, classified.
 *
 * `terminal` says whether the control should come back on its own. It is the one
 * thing the codes buy that the sentences cannot express, and it is why they are
 * matched at all.
 */
export interface ResendRefusal {
  kind: 'not-pending' | 'too-recently' | 'status-unrecognised';
  terminal: boolean;
  /** The server's own sentence. Render it; do not replace it. */
  message: string;
  /** Present only on `too-recently`, and only when the 429 carried usable fields. */
  cooldown: ResendCooldown | null;
}

/**
 * Classify a refusal by `error.code`.
 *
 * ⚠️ **Switch on the code, NEVER on message text.** The three sentences are the
 * server's to reword.
 *
 * ⚠️ **An unrecognised code returns `null`, and the caller MUST fall back to the
 * server's own message.** `null` means *"newer than this map"*, never *"not a
 * refusal"* — the same designed degradation as `quoteRefusalOf` in `@/lib/xero`.
 * Do not give it a default classification: guessing `terminal` wrong in the
 * `true` direction takes a working button away for good.
 */
export function resendRefusalOf(error: ApiError): ResendRefusal | null {
  switch (error.code) {
    case APPROVAL_NOT_PENDING_CODE:
      return { kind: 'not-pending', terminal: true, message: error.message, cooldown: null };
    case APPROVAL_RESENT_TOO_RECENTLY_CODE:
      return {
        kind: 'too-recently',
        terminal: false,
        message: error.message,
        cooldown: resendCooldownOf(error),
      };
    case APPROVAL_STATUS_UNRECOGNISED_CODE:
      return {
        kind: 'status-unrecognised',
        terminal: true,
        message: error.message,
        cooldown: null,
      };
    default:
      return null;
  }
}

/**
 * The extra line a cooldown earns, beside the server's sentence — never instead
 * of it.
 *
 * `null` when there is nothing to add, so a caller renders nothing rather than an
 * empty slot. The countdown itself is driven from `retryAfterSeconds` by the call
 * site; this is the wording for what the reader cannot otherwise see.
 */
export function cooldownNote(cooldown: ResendCooldown | null): string | null {
  if (!cooldown) return null;
  const parts: string[] = [];
  if (cooldown.lastRequestedAt !== null) {
    parts.push(`The last request went out at ${formatDateTime(cooldown.lastRequestedAt)}.`);
  }
  if (cooldown.nextAllowedAt !== null) {
    parts.push(`Another can be sent from ${formatDateTime(cooldown.nextAllowedAt)}.`);
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

/* --- the BILLING GATE ---------------------------------------------------- */

/**
 * ## The gate is SERVER-SIDE, and this file used to say the opposite
 *
 * ⚠️ **CORRECTED 2026-08-31.** Three form modals shipped with a client-side
 * refusal whose comments each stated *"this is a CLIENT rule and the server does
 * not enforce it"*. **Measured in `emerge-backend-app`: it does.**
 * `assertCompanyApproved(companyId, action, executor?)` in
 * `src/modules/companies/company-approval.guard.ts` — added by **migration 099**
 * — throws a **409** and is called from:
 *
 *   - `quotes.service.ts` → `createQuote`
 *   - `invoices.service.ts` → `createInvoice`
 *   - `quote-convert.ts` → quote → invoice
 *   - `deal-convert.ts` → deal → quote, and deal → invoice
 *
 * So the sentences those forms invented were a **second, parallel wording of a
 * refusal the server already words better**: the server's names the company,
 * says an approver has been emailed a link, and says the rest of the company
 * still works in the meantime. `CLAUDE.md`'s rule is exact — *surface the
 * server's sentence verbatim and keep no parallel sentence of ours to drift from
 * it* — so the client sentences are gone and this maps the codes instead.
 *
 * ⚠️ **DEALS ARE NOT GATED, and that is not an omission.** `deals.service.ts`
 * never calls the guard: a deal is a pipeline record, not a demand for money,
 * and the guard's own call-site list says so in terms. **`DealFormModal` keeps
 * its own refusal sentences, because on deals the client rule is the only rule.**
 * Do not "harmonise" the three forms onto this module — doing so deletes the one
 * rule deals have.
 *
 * ⚠️ **An ABSENT approval status is PERMITTED by the guard** (`reading.kind ===
 * 'absent'` returns early, for a database where migration 099 has not run). So
 * *"not approved"* and *"no status recorded"* are different facts, and nothing
 * here may treat the second as the first.
 */

/**
 * Awaiting a decision. **Recoverable** — an approver is holding a link.
 */
export const COMPANY_NOT_APPROVED_CODE = 'COMPANY_NOT_APPROVED';

/**
 * Refused during approval. **Not recoverable by waiting**, which is the whole
 * reason it is a second code rather than a second sentence under the first:
 * `pending` says *chase the approver*, `rejected` says *nothing you do on this
 * document will change that*. Rendering them alike leaves somebody waiting for
 * an email that is never coming.
 */
export const COMPANY_APPROVAL_REJECTED_CODE = 'COMPANY_APPROVAL_REJECTED';

/**
 * The company could not be read, or its status is a value the server cannot
 * interpret. Distinct from both of the above on purpose — nobody is holding a
 * link and nobody refused anything, so telling a person to chase an approver
 * sends them to a screen where there is nothing to find.
 */
export const COMPANY_APPROVAL_UNREADABLE_CODE = 'COMPANY_APPROVAL_UNREADABLE';

/** A billing-gate refusal, classified. */
export interface BillingGateRefusal {
  kind: 'not-approved' | 'rejected' | 'unreadable';
  /**
   * Whether waiting can clear it. `not-approved` clears when an approver acts;
   * the other two do not clear on their own.
   *
   * ⚠️ It gates nothing today — every caller renders the same banner — and it is
   * carried because it is the one thing the codes buy that the sentences cannot
   * express, exactly as {@link ResendRefusal.terminal} is.
   */
  terminal: boolean;
  /** The server's own sentence. **Render it; never replace or precede it.** */
  message: string;
}

/**
 * Classify a billing-gate refusal by `error.code`.
 *
 * ⚠️ **Switch on the code, NEVER on message text.** The three sentences are the
 * server's to reword, and two of them differ only in their middle clause.
 *
 * ⚠️ **An unrecognised code returns `null`, and the caller MUST fall back to the
 * server's own message.** `null` means *"newer than this branch"*, never *"not a
 * refusal"* — the same designed degradation as {@link resendRefusalOf} and
 * `quoteRefusalOf` in `@/lib/xero`. Do not give it a default classification.
 *
 * ⚠️ **The 409 carries NO `details`**, deliberately and on every call site: half
 * of them reach the gate with a company the caller never sent (off the deal, off
 * the quote, out of the URL), so a `path: 'companyId'` would name a control that
 * is not on the form — inflating the "one field needs attention" count and
 * highlighting nothing. **Every caller renders this as a FORM-LEVEL banner.**
 */
export function billingGateRefusalOf(error: ApiError): BillingGateRefusal | null {
  switch (error.code) {
    case COMPANY_NOT_APPROVED_CODE:
      return { kind: 'not-approved', terminal: false, message: error.message };
    case COMPANY_APPROVAL_REJECTED_CODE:
      return { kind: 'rejected', terminal: true, message: error.message };
    case COMPANY_APPROVAL_UNREADABLE_CODE:
      return { kind: 'unreadable', terminal: true, message: error.message };
    default:
      return null;
  }
}

/**
 * The billing gate's sentence, when the failure was one — `null` otherwise, so a
 * caller falls straight through to its ordinary error routing.
 *
 * The one line every save handler needs, so no call site has to remember that
 * this refusal must not be routed to a field.
 */
export function billingGateMessage(caught: unknown): string | null {
  if (!(caught instanceof ApiError)) return null;
  return billingGateRefusalOf(caught)?.message ?? null;
}
